<template>
    <v-card class="user-cal" :class="{ 'user-cal--page': page }" :flat="page">
        <!-- Header -->
        <div class="user-cal-header">
            <div class="d-flex align-center">
                <v-avatar size="44" color="#e0e0e0" class="mr-3">
                    <v-img v-if="pic" :src="pic" />
                    <span v-else class="uc-initials">{{ initials(name) }}</span>
                </v-avatar>
                <div>
                    <div class="text-h6 font-weight-bold">{{ name }}</div>
                    <div class="text-caption grey--text">{{ position }}</div>
                </div>
            </div>

            <div class="d-flex align-center">
                <v-btn icon small @click="year--"><v-icon>mdi-chevron-left</v-icon></v-btn>
                <span class="mx-2 font-weight-medium text-body-2" style="min-width: 190px; text-align: center;">
                    January to December {{ year }}
                </span>
                <v-btn icon small @click="year++"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </div>

            <v-btn
                v-if="canAddLeave"
                color="primary"
                depressed
                small
                class="text-none"
                @click="$emit('add-leave', null)"
            >
                <v-icon small left>mdi-plus</v-icon>Add leave
            </v-btn>

            <v-btn v-if="!page" icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-divider />

        <div class="user-cal-body">
            <!-- 12 mini months -->
            <div class="uc-months">
                <div v-for="m in months" :key="m.name" class="uc-mini">
                    <div class="uc-mtitle">{{ m.name }}</div>
                    <div class="uc-weekdays">
                        <span v-for="(w, i) in weekdayLetters" :key="i">{{ w }}</span>
                    </div>
                    <div class="uc-grid">
                        <div
                            v-for="(c, i) in m.cells"
                            :key="i"
                            class="uc-day"
                            :class="{
                                blank: c.blank,
                                weekend: c.weekend,
                                leave: !!c.leave && !c.isHalf,
                                half: c.isHalf,
                                wfh: !!c.leave && c.isWfh,
                                pending: !!c.leave && c.pending,
                                today: c.today,
                                clickable: !!c.leave,
                                addable: canAddOn(c),
                            }"
                            :style="dayStyle(c)"
                            @mouseenter="c.leave && showTip($event, c)"
                            @mouseleave="hoverTip = null"
                            @click="onDayClick(c)"
                        >
                            <span v-if="!c.blank">{{ c.dayNum }}</span>
                            <span v-if="c.leave && c.isWfh" class="uc-wfh-glyph" aria-hidden="true">⌂</span>
                            <span v-else-if="canAddOn(c)" class="uc-add-glyph" aria-hidden="true">+</span>
                        </div>
                    </div>
                </div>

                <!-- Legend (full page only): every leave type + how pending / half days look -->
                <div v-if="page" class="uc-legend">
                    <span v-for="lt in leaveTypes" :key="'lt' + lt.id" class="uc-legend-item">
                        <i
                            :class="{ 'uc-legend-wfh': isWfhLegend(lt) }"
                            :style="{ backgroundColor: legendColor(lt) }"
                        ><template v-if="isWfhLegend(lt)">⌂</template></i>{{ lt.name }}
                    </span>
                    <span class="uc-legend-item"><i class="uc-sw-pending"></i>Pending (awaiting approval)</span>
                    <span class="uc-legend-item"><i class="uc-sw-half"></i>Half day</span>
                    <span class="uc-legend-item"><i class="uc-sw-weekend"></i>Weekend</span>
                </div>
            </div>

            <!-- Sidebar -->
            <div class="uc-sidebar">
                <div class="uc-sidebar-title">Year end Dec {{ year }}</div>

                <v-card outlined class="uc-panel mb-3">
                    <div class="uc-panel-head">
                        <span>
                            Allowance
                            <v-btn
                                v-if="canEditAllowance"
                                x-small
                                text
                                color="primary"
                                class="text-none ml-1 uc-edit-allowance"
                                @click="$emit('edit-allowance', year)"
                            >
                                <v-icon x-small left>mdi-pencil</v-icon>Edit
                            </v-btn>
                        </span>
                        <span>Days</span>
                    </div>
                    <div v-if="allowanceRows.length === 0" class="uc-panel-row grey--text">
                        <span>No allowance set for {{ year }}</span><span>—</span>
                    </div>
                    <div v-for="row in allowanceRows" :key="row.name" class="uc-panel-row">
                        <span>{{ row.name }}</span><span>{{ fmtNum(row.allowance) }}</span>
                    </div>
                    <v-divider v-if="allowanceRows.length" />
                    <div class="uc-panel-row font-weight-bold">
                        <span>Total allowance</span><span>{{ fmtNum(totalAllowance) }}</span>
                    </div>
                </v-card>

                <v-card outlined class="uc-panel mb-3">
                    <div class="uc-panel-head">
                        <span>Deductions</span><span>Days</span>
                    </div>
                    <div v-for="row in allowanceRows" :key="row.name" class="uc-panel-row">
                        <span>{{ row.name }}</span><span>{{ fmtNum(row.used) }}</span>
                    </div>
                    <div class="uc-panel-row font-weight-bold">
                        <span>Total deductions</span><span>{{ fmtNum(totalUsed) }}</span>
                    </div>
                    <v-divider />
                    <div class="uc-panel-row font-weight-bold">
                        <span>Days remaining</span>
                        <span class="text-h6" :class="totalRemaining < 0 ? 'error--text' : ''">{{ fmtNum(totalRemaining) }}</span>
                    </div>
                </v-card>

                <v-card outlined class="uc-panel">
                    <div class="uc-panel-head"><span>Time off</span></div>
                    <div class="uc-spark">
                        <div v-for="(v, i) in monthlyTotals" :key="i" class="uc-spark-col" :title="`${monthNames[i]}: ${fmtNum(v)} day(s)`">
                            <div class="uc-spark-bar" :style="{ height: (v / sparkMax * 100) + '%' }"></div>
                        </div>
                    </div>
                    <div class="uc-spark-labels">
                        <span v-for="(w, i) in monthLetters" :key="i">{{ w }}</span>
                    </div>
                </v-card>
            </div>
        </div>

        <!-- Hover tooltip -->
        <div v-if="hoverTip" class="uc-tip" :style="{ left: hoverTip.x + 'px', top: hoverTip.y + 'px' }">
            {{ hoverTip.text }}
        </div>

        <!-- Leave details -->
        <LeaveDetailModal
            :request="detailRequest"
            :leave-types="leaveTypes"
            @close="detailRequest = null"
        />
    </v-card>
</template>

<script>
import { colorForType, fullName, initials, profilePicUrl, fmtNum, isApproved, requestUserId, isWfhRequest, isWfhType } from './leaveUtils';
import LeaveDetailModal from './LeaveDetailModal.vue';

// Requests created before weekends started counting as leave days (UTC).
// Their stored counts were weekday-only, so the totals keep that math for them.
const WEEKENDS_COUNT_FROM = '2026-10-04T05:30:00';

export default {
    name: 'UserLeaveCalendar',
    components: { LeaveDetailModal },
    props: {
        user: { type: Object, required: true },
        requests: { type: Array, default: () => [] },
        balances: { type: Array, default: () => [] },
        leaveTypes: { type: Array, default: () => [] },
        initialYear: { type: Number, default: () => new Date().getFullYear() },
        page: { type: Boolean, default: false },
        // Show an "Edit" button on the allowance panel (the parent decides who may; the API enforces it).
        canEditAllowance: { type: Boolean, default: false },
        // Let the viewer log (pre-approved) leave for this person: click a free day or use the button.
        canAddLeave: { type: Boolean, default: false },
    },
    data() {
        return {
            year: this.initialYear,
            hoverTip: null,
            detailRequest: null,
            weekdayLetters: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
            monthLetters: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
            monthNames: ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'],
        };
    },
    computed: {
        name() {
            return fullName(this.user);
        },
        position() {
            return this.user.position_name || (this.user.role && this.user.role.name) || this.user.role_name || '—';
        },
        pic() {
            return profilePicUrl(this.user);
        },
        userApprovedRequests() {
            const uid = this.user.id;
            return (this.requests || []).filter((r) => requestUserId(r) === uid && isApproved(r));
        },
        userVisibleRequests() {
            const uid = this.user.id;
            return (this.requests || []).filter((r) => {
                if (requestUserId(r) !== uid) return false;
                if (isApproved(r)) return true;
                // On the user's own /my-calendar page, also surface their still
                // pending requests (rendered faded) so a just-submitted request
                // shows up straight away.
                return this.page && (((r.status && r.status.id) || r.leave_status_id) === 1);
            });
        },
        // { 'YYYY-MM-DD': { request, color, isHalf, side, isWfh, pending } }
        dayMap() {
            const map = {};
            this.userVisibleRequests.forEach((r) => {
                const f = (r.from || '').substring(0, 10);
                const t = (r.to || '').substring(0, 10);
                if (!f || !t || f > t) return;
                const color = colorForType(r.leave_type_id || (r.leave_type && r.leave_type.id), this.leaveTypes);
                const isHalf = !!r.is_half_day && f === t;
                const side = r.half_day_period === 'PM' ? 'pm' : 'am';
                const isWfh = isWfhRequest(r, this.leaveTypes);
                const pending = !isApproved(r);
                let cur = this.$date(f);
                let guard = 0;
                while (cur.format('YYYY-MM-DD') <= t && guard < 400) {
                    map[cur.format('YYYY-MM-DD')] = { request: r, color, isHalf, side, isWfh, pending };
                    cur = cur.add(1, 'day');
                    guard += 1;
                }
            });
            return map;
        },
        months() {
            const todayKey = this.$date().format('YYYY-MM-DD');
            const out = [];
            for (let mo = 0; mo < 12; mo++) {
                const mm = String(mo + 1).padStart(2, '0');
                const first = this.$date(`${this.year}-${mm}-01`);
                const daysInMonth = first.endOf('month').date();
                const startOffset = (first.day() + 6) % 7; // Monday-first
                const cells = [];
                for (let i = 0; i < startOffset; i++) cells.push({ blank: true });
                for (let dn = 1; dn <= daysInMonth; dn++) {
                    const key = `${this.year}-${mm}-${String(dn).padStart(2, '0')}`;
                    const dow = this.$date(key).day();
                    const seg = this.dayMap[key];
                    cells.push({
                        dayNum: dn,
                        key,
                        weekend: dow === 0 || dow === 6,
                        today: key === todayKey,
                        leave: seg ? seg.request : null,
                        color: seg ? seg.color : null,
                        isHalf: seg ? seg.isHalf : false,
                        side: seg ? seg.side : null,
                        isWfh: seg ? seg.isWfh : false,
                        pending: seg ? seg.pending : false,
                    });
                }
                out.push({ name: this.monthNames[mo], cells });
            }
            return out;
        },
        balanceRecord() {
            return (this.balances || []).find((u) => u.id === this.user.id) || null;
        },
        allowanceRows() {
            const rec = this.balanceRecord;
            if (!rec || !rec.get_leaves) return [];
            return rec.get_leaves
                .filter((l) => l.year === this.year)
                .map((l) => ({
                    name: (l.leave_type && l.leave_type.name) || 'Leave',
                    allowance: l.leave_count || 0,
                    used: l.used_leaves || 0,
                    remaining: (l.leave_count || 0) - (l.used_leaves || 0),
                }));
        },
        totalAllowance() {
            return this.allowanceRows.reduce((s, r) => s + r.allowance, 0);
        },
        totalUsed() {
            return this.allowanceRows.reduce((s, r) => s + r.used, 0);
        },
        totalRemaining() {
            return this.totalAllowance - this.totalUsed;
        },
        monthlyTotals() {
            const totals = new Array(12).fill(0);
            this.userApprovedRequests.forEach((r) => {
                const f = (r.from || '').substring(0, 10);
                const t = (r.to || '').substring(0, 10);
                if (!f || !t || f > t) return;
                const created = String(r.created_at || '').replace(' ', 'T').substring(0, 19);
                const skipWeekends = !created || created < WEEKENDS_COUNT_FROM;
                let cur = this.$date(f);
                let guard = 0;
                while (cur.format('YYYY-MM-DD') <= t && guard < 400) {
                    const dow = cur.day();
                    if (cur.year() === this.year && !(skipWeekends && (dow === 0 || dow === 6))) {
                        totals[cur.month()] += r.is_half_day ? 0.5 : 1;
                    }
                    cur = cur.add(1, 'day');
                    guard += 1;
                }
            });
            return totals;
        },
        sparkMax() {
            return Math.max(1, ...this.monthlyTotals);
        },
    },
    methods: {
        isWfhLegend(lt) {
            return isWfhType(lt);
        },
        legendColor(lt) {
            return colorForType(lt.id, this.leaveTypes);
        },
        fmtNum,
        initials,
        // On the /my-calendar page, an empty (no-leave) day is a shortcut to
        // request leave / WFH for that date.
        // A manager viewing someone else's calendar gets the same shortcut to log leave for them.
        canAddOn(c) {
            return (this.page || this.canAddLeave) && !c.blank && !c.leave;
        },
        onDayClick(c) {
            if (c.blank) return;
            if (c.leave) {
                this.detailRequest = c.leave;
            } else if (this.canAddOn(c)) {
                this.$emit(this.canAddLeave ? 'add-leave' : 'add-request', c.key);
            }
        },
        dayStyle(c) {
            if (!c.leave) return {};
            // Work From Home is a flat block in its own colour, like any leave
            // type — the ⌂ glyph is what marks it.
            if (c.isHalf) {
                const stops =
                    c.side === 'pm'
                        ? `transparent 0 50%, ${c.color} 50% 100%`
                        : `${c.color} 0 50%, transparent 50% 100%`;
                return { backgroundImage: `linear-gradient(to right, ${stops})` };
            }
            return { backgroundColor: c.color };
        },
        tipText(c) {
            const r = c.leave;
            const type = (r.leave_type && r.leave_type.name) || 'Leave';
            const f = this.$date((r.from || '').substring(0, 10)).format('MMM D, YYYY');
            const t = this.$date((r.to || '').substring(0, 10)).format('MMM D, YYYY');
            const range = r.is_half_day
                ? `${f} (${r.half_day_period === 'PM' ? 'Afternoon' : 'Morning'})`
                : (f === t ? f : `${f} – ${t}`);
            const dur = r.is_half_day ? '0.5 day' : `${r.count || 0} day${(r.count || 0) === 1 ? '' : 's'}`;
            const pending = c.pending ? ' · Pending approval' : '';
            return `${type} · ${range} (${dur})${pending}`;
        },
        showTip(e, c) {
            const pad = 14;
            let x = e.clientX + pad;
            const y = e.clientY + pad;
            if (x > window.innerWidth - 260) x = e.clientX - 260;
            this.hoverTip = { text: this.tipText(c), x, y };
        },
    },
};
</script>

<style lang="scss" scoped>
.user-cal {
    display: flex;
    flex-direction: column;
    max-height: 82vh;
}

.user-cal--page {
    max-height: none;
}

.user-cal--page .user-cal-body {
    overflow-y: visible;
}

.user-cal--page .uc-sidebar {
    top: 84px;
}

.user-cal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    flex-wrap: wrap;
}

.uc-initials {
    color: #616161;
    font-size: 13px;
    font-weight: 700;
}

.user-cal-body {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    padding: 18px;
    overflow-y: auto;
}

.uc-months {
    flex: 1 1 auto;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px 18px;
    min-width: 0;
}

.uc-legend {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    align-items: center;
    padding-top: 4px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    .uc-legend-item {
        display: inline-flex;
        align-items: center;
        font-size: 12px;
        color: #616161;

        i {
            width: 14px;
            height: 14px;
            border-radius: 4px;
            margin-right: 6px;
            display: inline-block;
        }

        i.uc-legend-wfh {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-style: normal;
            line-height: 1;
            color: rgba(255, 255, 255, 0.9);
        }

        i.uc-sw-pending {
            background: #9e9e9e;
            opacity: 0.4;
        }

        i.uc-sw-half {
            background: linear-gradient(90deg, #9e9e9e 50%, #fff 50%);
            border: 1px solid #bdbdbd;
        }

        i.uc-sw-weekend {
            background: #f2f2f2;
            border: 1px solid #d0d0d0;
        }
    }
}

.uc-mini {
    min-width: 0;
}

.uc-mtitle {
    text-align: center;
    font-weight: 700;
    font-size: 13px;
    color: #5c6bc0;
    margin-bottom: 6px;
}

.uc-weekdays,
.uc-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
}

.uc-weekdays span {
    text-align: center;
    font-size: 10px;
    color: #b0bec5;
    padding: 2px 0;
}

.uc-day {
    text-align: center;
    font-size: 11px;
    color: #546e7a;
    padding: 5px 0;
    line-height: 1.1;

    &.weekend {
        background: #f2f2f2;
    }

    &.blank {
        background: transparent;
    }

    &.leave {
        color: #fff;
        font-weight: 700;
        border-radius: 3px;
    }

    &.half {
        color: #37474f;
        font-weight: 700;
        border-radius: 3px;
    }

    &.wfh {
        position: relative;
    }

    // A not-yet-approved request on the user's own calendar: same block, faded.
    &.pending {
        opacity: 0.4;
    }

    .uc-wfh-glyph {
        position: absolute;
        top: 0;
        right: 2px;
        font-size: 8px;
        line-height: 1;
        color: rgba(255, 255, 255, 0.9);
        pointer-events: none;
    }

    &.half .uc-wfh-glyph {
        color: rgba(0, 0, 0, 0.55);
    }

    &.clickable {
        cursor: pointer;
    }

    &.addable {
        position: relative;
        cursor: pointer;
        transition: background 0.12s;

        &:hover {
            background: #e8f0fe;
        }

        .uc-add-glyph {
            position: absolute;
            top: 0;
            right: 3px;
            font-size: 10px;
            line-height: 1;
            font-weight: 700;
            color: #1976d2;
            opacity: 0;
            transition: opacity 0.12s;
            pointer-events: none;
        }

        &:hover .uc-add-glyph {
            opacity: 1;
        }
    }

    &.today {
        outline: 1.5px solid #1976d2;
        outline-offset: -1.5px;
        border-radius: 3px;
        color: #1976d2;
        font-weight: 700;
    }
}

.uc-sidebar {
    width: 260px;
    flex: none;
    position: sticky;
    top: 0;
    align-self: flex-start;
}

.uc-sidebar-title {
    font-size: 13px;
    color: #78909c;
    margin-bottom: 10px;
}

.uc-panel {
    padding: 10px 14px;
    border-radius: 10px;
}

.uc-panel-head {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #90a4ae;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding-bottom: 6px;
}

.uc-panel-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    padding: 5px 0;
}

.uc-spark {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 44px;
    margin-top: 4px;
}

.uc-spark-col {
    flex: 1 1 0;
    height: 100%;
    display: flex;
    align-items: flex-end;
}

.uc-spark-bar {
    width: 100%;
    min-height: 2px;
    background: #c2185b;
    border-radius: 2px 2px 0 0;
}

.uc-spark-labels {
    display: flex;
    margin-top: 4px;

    span {
        flex: 1 1 0;
        text-align: center;
        font-size: 9px;
        color: #b0bec5;
    }
}

.uc-tip {
    position: fixed;
    z-index: 2100;
    max-width: 260px;
    padding: 6px 10px;
    background: #23232e;
    color: #fff;
    font-size: 12px;
    line-height: 1.35;
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
    pointer-events: none;
}

@media (max-width: 1100px) {
    .uc-months {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 860px) {
    .user-cal-body {
        flex-direction: column;
    }

    .uc-sidebar {
        width: 100%;
        position: static;
    }
}
</style>

<style>
/* Keep the tall calendar dialog clear of the app's fixed top nav */
.uc-dialog-shell {
    align-self: flex-start;
    margin-top: 88px;
}
</style>
